// src/utils/serviceWorkerAuth.ts
import { LOG_STYLES } from './consoleColors';

/**
 * ServiceWorkerAuth - Handles communication with Service Worker for token storage
 *
 * Provides methods to:
 * - Store access token in SW (persists across page refresh)
 * - Retrieve access token from SW (after page refresh)
 * - Clear access token from SW (on logout)
 * - Listen for logout events from SW
 *
 * Uses MessageChannel for bi-directional communication with SW
 */

const CONTROLLER_WAIT_MS = 200;
const SEND_MESSAGE_TIMEOUT_MS = 500;

class ServiceWorkerAuth {
  private registration: ServiceWorkerRegistration | null = null;

  private isInitialized = false;

  private initPromise: Promise<void> | null = null;

  // Single message listener (prevents duplicate registration on re-init)
  private logoutCallbacks: Array<(reason: string) => void> = [];

  private logoutListenerAttached = false;

  /**
   * Initialize Service Worker connection
   * Waits for SW to be ready and for controller to be available
   *
   * Called once during app initialization (App.tsx)
   */
  async init(): Promise<void> {
    // Return existing promise if already initializing
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      if (!('serviceWorker' in navigator)) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[SW] Service Worker not supported',
            LOG_STYLES.verbose
          );
        }
        return;
      }

      try {
        // Wait for Service Worker to be ready (VitePWA registers it automatically)
        this.registration = await navigator.serviceWorker.ready;
        if (process.env.NODE_ENV !== 'production') {
          console.log('%c[SW] Service Worker ready', LOG_STYLES.info);
        }

        // Wait for controller (with timeout)
        if (!navigator.serviceWorker.controller) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('%c[SW] Waiting for controller...', LOG_STYLES.verbose);
          }

          await new Promise<void>((resolve) => {
            // Already has controller
            if (navigator.serviceWorker.controller) {
              if (process.env.NODE_ENV !== 'production') {
                console.log(
                  '%c[SW] Controller already available',
                  LOG_STYLES.verbose
                );
              }
              resolve();
              return;
            }

            // Timeout after 2 seconds
            const timeout = setTimeout(() => {
              if (process.env.NODE_ENV !== 'production') {
                console.log(
                  '%c[SW] Controller wait timeout, continuing anyway',
                  LOG_STYLES.verbose
                );
              }
              resolve();
            }, CONTROLLER_WAIT_MS);

            // Listen for controller change
            navigator.serviceWorker.addEventListener(
              'controllerchange',
              () => {
                clearTimeout(timeout);
                if (process.env.NODE_ENV !== 'production') {
                  console.log(
                    '%c[SW] Controller changed, now available',
                    LOG_STYLES.info
                  );
                }
                resolve();
              },
              { once: true }
            );
          });
        }

        this.attachLogoutListener();
        this.isInitialized = true;

        if (process.env.NODE_ENV !== 'production') {
          console.log('%c[SW] ✅ Initialization complete', LOG_STYLES.success);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('%c[SW] ❌ Init failed:', LOG_STYLES.error, error);
        }
        // Do NOT rethrow — caller should still proceed with memory-only auth
        // throw error;
      }
    })();

    return this.initPromise;
  }

  /**
   * Check if Service Worker is ready for communication
   */
  isReady(): boolean {
    return this.isInitialized && !!this.registration;
  }

  /**
   * Send message to Service Worker and wait for response
   * Uses MessageChannel for two-way communication
   *
   * @param message - Message object to send to SW
   * @returns Promise that resolves with SW response
   */
  private async sendMessage(message: any): Promise<any> {
    // Use controller if available, otherwise use registration.active
    const target =
      navigator.serviceWorker.controller || this.registration?.active;

    if (!target) {
      throw new Error('Service Worker not available');
    }

    return new Promise((resolve, reject) => {
      // Create message channel for response
      const messageChannel = new MessageChannel();
      let settled = false;

      const cleanup = () => {
        settled = true;
        messageChannel.port1.onmessage = null;
        messageChannel.port1.close();
      };

      // Listen for response
      messageChannel.port1.onmessage = (event) => {
        if (settled) return;
        cleanup();
        if (event.data?.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data);
        }
      };

      try {
        target.postMessage(message, [messageChannel.port2]);
      } catch (err) {
        cleanup();
        reject(err);
        return;
      }

      // Timeout after 5 seconds
      setTimeout(() => {
        if (settled) return;
        cleanup();
        reject(new Error('Service Worker message timeout'));
      }, SEND_MESSAGE_TIMEOUT_MS);
    });
  }

  /**
   * Store access token in Service Worker
   *
   * @param token - Access token JWT string
   * @returns Promise<boolean> - true if successful
   */
  async setAccessToken(token: string): Promise<boolean> {
    try {
      const response = await this.sendMessage({ type: 'SET_TOKEN', token });
      return !!response?.success;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('%c[SW] Failed to set token:', LOG_STYLES.verbose, error);
      }
      return false;
    }
  }

  /**
   * Retrieve access token from Service Worker
   *
   * @returns Promise<string | null> - Access token or null if not found
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const response = await this.sendMessage({ type: 'GET_TOKEN' });
      return response?.token ?? null;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '%c[SW] ❌ Failed to get token:',
          LOG_STYLES.error,
          error
        );
      }
      return null;
    }
  }

  /**
   * Clear access token from Service Worker
   * Called on logout
   *
   * @returns Promise<boolean> - true if successful
   */
  async clearAccessToken(): Promise<boolean> {
    try {
      const response = await this.sendMessage({ type: 'CLEAR_TOKEN' });
      return !!response?.success;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '%c[SW] ❌ Failed to clear token:',
          LOG_STYLES.error,
          error
        );
      }
      return false;
    }
  }

  /**
   * Listen for logout events from Service Worker
   * SW can trigger logout if it detects token expiry or other issues
   *
   * @param callback - Function to call when logout event received
   */
  onLogout(callback: (reason: string) => void): () => void {
    this.logoutCallbacks.push(callback);
    return () => {
      this.logoutCallbacks = this.logoutCallbacks.filter((c) => c !== callback);
    };
  }

  private attachLogoutListener(): void {
    if (this.logoutListenerAttached) return;
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'LOGOUT') {
        this.logoutCallbacks.forEach((cb) => {
          try {
            cb(event.data.reason);
          } catch (e) {
            if (process.env.NODE_ENV !== 'production') {
              console.error('[SW] Logout callback error:', e);
            }
          }
        });
      }
    });

    this.logoutListenerAttached = true;
  }
}

export const swAuth = new ServiceWorkerAuth();
